import { telegramInitData } from './telegram.ts'

/**
 * Mini App token lives in memory only: every launch brings fresh initData, so there is nothing
 * worth persisting and nothing for other scripts to steal from storage.
 */
let bearerToken: string | null = null
let miniAppInvitation: string | null = null
export const getMiniAppInvitation = () => miniAppInvitation
export const getBearerToken = () => bearerToken

export type Provider = 'telegram' | 'google' | 'email'

async function post<T>(path: string, body: unknown): Promise<{ status: number; data: T }> {
  const res = await fetch(`/api/auth/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = res.status === 204 ? ({} as T) : ((await res.json().catch(() => ({}))) as T)
  return { status: res.status, data }
}

/** Telegram Mini App: trades initData for a bearer token. Returns false outside Telegram. */
export async function signInWithMiniApp(): Promise<boolean> {
  const initData = telegramInitData()
  if (!initData) return false
  const { status, data } = await post<{ token?: string; invitation?: string }>(
    'telegram/mini-app',
    { initData },
  )
  if (status !== 200 || !data.token) return false
  bearerToken = data.token
  miniAppInvitation = data.invitation ?? null
  return true
}

/** Full-page redirect: the provider's consent screen can't run inside our page. */
export function startOAuth(
  provider: 'telegram' | 'google',
  options: { mode?: 'login' | 'link'; next?: string } = {},
) {
  const params = new URLSearchParams()
  if (options.mode) params.set('mode', options.mode)
  if (options.next) params.set('next', options.next)
  window.location.assign(`/api/auth/${provider}/start?${params}`)
}

export type EmailStartResult =
  | { ok: true; resendIn: number }
  | { ok: false; error: 'invalid_email' | 'rate_limited' | 'unknown'; retryAfter?: number }

export async function sendEmailCode(
  email: string,
  purpose: 'login' | 'link' = 'login',
): Promise<EmailStartResult> {
  const { status, data } = await post<{ resendIn?: number; error?: string; retryAfter?: number }>(
    'email/start',
    { email, purpose },
  )
  if (status === 202) return { ok: true, resendIn: data.resendIn ?? 60 }
  if (status === 400) return { ok: false, error: 'invalid_email' }
  if (status === 429) return { ok: false, error: 'rate_limited', retryAfter: data.retryAfter }
  return { ok: false, error: 'unknown' }
}

export type EmailVerifyResult =
  | { ok: true }
  | {
      ok: false
      error:
        | 'invalid_code'
        | 'expired'
        | 'too_many_attempts'
        | 'rate_limited'
        | 'conflict'
        | 'provider_taken'
        | 'suspended'
        | 'unknown'
      attemptsLeft?: number
    }

export async function verifyEmailCode(
  email: string,
  code: string,
  purpose: 'login' | 'link' = 'login',
): Promise<EmailVerifyResult> {
  const { status, data } = await post<{ error?: string; attemptsLeft?: number }>('email/verify', {
    email,
    code,
    purpose,
  })
  if (status === 200) return { ok: true }
  const known = [
    'invalid_code',
    'expired',
    'too_many_attempts',
    'rate_limited',
    'conflict',
    'provider_taken',
    'suspended',
  ] as const
  const error = known.find((k) => k === data.error) ?? 'unknown'
  return { ok: false, error, attemptsLeft: data.attemptsLeft }
}

export async function signOut(): Promise<void> {
  await post('logout', {})
  bearerToken = null
}
